<template>
  <!-- TODO: move to external component -->
  <div v-if="isLoading" class="loader-wrapper">
    <div class="header-2 loader-description">
      Please wait
    </div>

    <div v-if="isProcessing" class="caption loader-description">
      Your animation is being processed
    </div>

    <div v-if="!isProcessing" class="caption loader-description">
      Your animation is being downloaded
    </div>

    <div class="progress-placeholder">
      <svg class="progress-circle" width="100" height="100">
        <defs>
          <radialGradient id="progress-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="26%" stop-color="#20AD8B" />
            <stop offset="76%" stop-color="#ACE41B" />
          </radialGradient>
        </defs>
        <g transform="rotate(-90, 50, 50)">
          <circle
            class="progress-circle-bg"
            cx="50"
            cy="50"
            r="45"
            fill="transparent"
            stroke="#eee"
            stroke-width="10"
          />
          <circle
            class="progress-circle-fg"
            cx="50"
            cy="50"
            r="45"
            fill="transparent"
            stroke="url(#progress-gradient)"
            stroke-width="10"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="
              circumference - loadingProgress * circumference
            "
            stroke-linecap="round"
          />
        </g>
      </svg>
    </div>

    <div v-if="isLoading && !isProcessing" class="progress-label">
      {{ formattedLoadedSize }} / {{ formattedTotalSize }} ({{ Math.round(loadingProgress * 100) }}%)
    </div>
  </div>

  <div v-if="!isLoading" class="animation-wrapper">
    <div class="anim-controls">
      <HoverPopover message="Show/hide the character's skeleton">
        <div class="control-btn" @click="toggleSkeletonVisibility">
          <BaseIcon iconName="skull"></BaseIcon>
        </div>
      </HoverPopover>

      <HoverPopover message="Rotate/Pan view">
        <div class="control-btn" @click="togglePanMode">
          <BaseIcon :iconName="isPanModeEnabled ? 'pan' : 'rotate'"></BaseIcon>
        </div>
      </HoverPopover>

      <HoverPopover message="Reset camera position">
        <div class="control-btn" @click="resetCamera">
          <BaseIcon iconName="camera"></BaseIcon>
        </div>
      </HoverPopover>
    </div>

    <div ref="threejsContainer" class="threejs-container" @dragover.prevent="handleDragOver"
      @drop.prevent="handleFileDrop" v-if="!props.animationId">
      You can drag and drop an FBX or GLB file here:
    </div>
    <div ref="threejsContainer" class="threejs-container" v-else></div>

    <div class="scale-controls">
      <HoverPopover message="Zoom in">
        <div class="control-btn" @click="zoomIn">
          <BaseIcon iconName="plus"></BaseIcon>
        </div>
      </HoverPopover> 
      <HoverPopover message="Zoom out">
        <div class="control-btn" @click="zoomOut">
          <BaseIcon iconName="minus"></BaseIcon>
        </div>
      </HoverPopover>
    </div>
  </div>

  <div v-if="!isLoading" class="animation-footer">
    <div class="control-btn locked" @click="handleHomeButton">
      <BaseIcon iconName="layers"
                color="rgba(255,255,255,0.4)">
      </BaseIcon>
    </div>

    <div class="playbar">
      <div class="progress-container" 
          @click="setAnimationProgress($event)" 
          @mousedown="startDragging" 
          @mouseup="stopDragging" 
          @mouseleave="stopDragging"
          @mousemove="dragUpdate">
        <div class="progress-bar" :style="{ width: progressBarWidth + '%' }"></div>
      </div>

      <div class="time-codes">
        <div>{{ formattedCurrentTime }}</div>
        <div>{{ formattedDuration }}</div>
      </div>
      <div class="playbar-controls">
        <div class="control-btn rotate" @click="previousFrame">
          <BaseIcon iconName="fastForward"></BaseIcon>
        </div>
        <div class="control-btn" @click="toggleAnimation">
          <BaseIcon :iconName="iconName"></BaseIcon>
        </div>
        <div class="control-btn" @click="nextFrame">
          <BaseIcon iconName="fastForward"></BaseIcon>
        </div>
        <div class="control-btn" @click="resetToFirstFrame">
          <BaseIcon iconName="rotateLeft"></BaseIcon>
        </div>
      </div>
    </div>

    <HoverPopover
      message="Do you have any questions regarding our service? If you have suggestions to share with us please send an email to <span class='gradient-text'>support@avacapo.com</span>">
      <div class="control-btn" @click="handleHomeButton">
        <BaseIcon iconName="help"></BaseIcon>
      </div>
    </HoverPopover>
  </div>
</template>

<script setup>
import BaseIcon from "@/components/BaseIcon.vue";
import HoverPopover from "@/components/HoverPopover.vue";

import { onMounted, onUnmounted, ref, reactive, computed, watch } from "vue";
import { useStore } from "vuex";

const store = useStore();

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const loaderRadius = 45;
const circumference = computed(() => 2 * Math.PI * loaderRadius);

const totalSize = ref(0); 
const loadedSize = ref(0); 

const formattedLoadedSize = computed(() => formatSize(loadedSize.value));
const formattedTotalSize = computed(() => formatSize(totalSize.value));

function formatSize(size) {
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const emit = defineEmits(
  ["loading-state-changed"]
);

const props = defineProps({
  animationId: String,
});

const threejsContainer = ref(null);
const clock = new THREE.Clock();
let scene, camera, renderer, controls, loadedScene, skeletonMesh;
const mixers = [];
let animationActions = []; 

const initialCameraPosition = new THREE.Vector3(0, 5, 10);

const iconName = ref("pause");
const isPanModeEnabled = ref(false);
const zoomStep = 1;

const progressBarWidth = ref(0);
const animationDetails = reactive({
  duration: 0,
  currentTime: 0,
});
const isAnimationPlaying = ref(true);

const isLoading = ref(false);

watch(isLoading, (newValue) => {
  emit("loading-state-changed", newValue);
});

const isProcessing = ref(false);
const loadingProgress = ref(0);

const formattedCurrentTime = computed(() =>
  formatTime(animationDetails.currentTime)
);
const formattedDuration = computed(() => formatTime(animationDetails.duration));

const currentFrame = ref(0);
const totalFrames = computed(() => {
  if (animationActions.length > 0 && animationActions[0].getClip()) {
    return animationActions[0].getClip().duration * 30;
  }
  return 0;
});

let modelBuffer = null;

const dragging = ref(false);

function nextFrame() {
  if (currentFrame.value < totalFrames.value - 1) {
    currentFrame.value++;
    updateAnimationFrame();
  }
}

function previousFrame() {
  if (currentFrame.value > 0) {
    currentFrame.value--;
    updateAnimationFrame();
  }
}

function resetToFirstFrame() {
  currentFrame.value = 0;
  updateAnimationFrame();
}

function updateAnimationFrame() {
  const timePerFrame = 1 / 30; 
  const newTime = currentFrame.value * timePerFrame;
  animationActions.forEach(action => {
    action.paused = true;
    action.time = newTime;
    action.play(); 
    action.paused = true; 
  });

  animationDetails.currentTime = newTime;
  progressBarWidth.value = (newTime / animationDetails.duration) * 100;
}


onMounted(() => {
  if (!props.animationId) {
    initThreeJS();
    addGridFloor();
    console.warn("No animation ID provided! Debug mode ON");
  } else {
    const sessionId = store.getters.getSessionId;
    loadModel(sessionId);
  }

  window.addEventListener("resize", onWindowResize);
});

onUnmounted(() => {
  if (renderer) renderer.dispose();
  window.removeEventListener("resize", onWindowResize);
});

function onWindowResize() {
  const width = threejsContainer.value.clientWidth;
  const height = threejsContainer.value.clientHeight;

  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
}

function initThreeJS() {
  const width = threejsContainer.value.clientWidth;
  const height = threejsContainer.value.clientHeight;

  scene = new THREE.Scene();
  scene.background = new THREE.Color("rgb(220, 220, 220)");
  camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
  camera.position.set(...initialCameraPosition.toArray());

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(width, height);
  threejsContainer.value.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.enablePan = true;

  setupLights();
  animate();
}

function setupLights() {
  const ambientLight = new THREE.AmbientLight(0xcccccc, 0.4);
  const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight1.position.set(1, 1, 0).normalize();

  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight2.position.set(-1, 1, 0).normalize(); 

  const directionalLight3 = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight3.position.set(0, 1, 1).normalize();

  scene.add(ambientLight, directionalLight1, directionalLight2, directionalLight3);
}

async function loadModel(sessionId) {
  isLoading.value = true;

  const modelUrl = `${process.env.VUE_APP_BACKEND_URL}/download-animation/?hashed_id=${props.animationId}&extension=glb`;

  try {
    const response = await fetch(modelUrl, {
      method: "GET",
      headers: {
        'Content-Type': 'application/json',
        'session-id': sessionId
      },
    });

    if (response.status === 206) {
      isProcessing.value = true;

      console.warn("Partial content received (206). Retrying after a delay...");

      loadingProgress.value += 2/100;
      
      setTimeout(() => {
        loadModel(sessionId); 
      }, 2000);
      return; 
    } 
    
    if (!response.ok) {
      throw new Error("Failed to load model");
    }

    isProcessing.value = false;

    const contentLength = response.headers.get("content-length");
    totalSize.value = parseInt(contentLength, 10);
    let receivedLength = 0;
    const reader = response.body.getReader();
    
    const stream = new ReadableStream({
      start(controller) {
        function push() {
          reader.read().then(({ done, value }) => {
            if (done) {
              isLoading.value = false;
              controller.close();
              return;
            }

            receivedLength += value.length;
            loadedSize.value = receivedLength;
            loadingProgress.value = receivedLength / contentLength;

            controller.enqueue(value);
            push();
          }).catch((error) => {
            console.error("Error while reading the stream:", error);
            isLoading.value = false;
            controller.error(error);
          });
        }
        push();
      },
    });

    const buffer = await new Response(stream).arrayBuffer();

    modelBuffer = buffer;
    const isGLB = checkIfGLB(buffer);
    initThreeJS();
    addGridFloor();

    if (isGLB) {
      const loader = new GLTFLoader();
      loader.parse(buffer, "", (gltf) => {
        handleGLTFLoaded(gltf);
      }, (error) => {
        console.error("Error parsing GLTF model:", error);
      });
    } else {
      const loader = new FBXLoader();
      const fbx = loader.parse(buffer, "");
      handleFBXLoaded(fbx);
    }

  } catch (error) {
    console.error("Error loading model:", error);
    isLoading.value = false;
  }
}

function checkIfGLB(buffer) {
  const header = new Uint8Array(buffer, 0, 4);
  const magic = new TextDecoder("utf-8").decode(header);
  return magic === "glTF";
}

function handleFBXLoaded(fbx) {
  scene.remove(loadedScene);
  fbx.scale.z = -1;
  loadedScene = fbx;
  loadedScene.scale.x = -loadedScene.scale.x;
  scene.add(loadedScene);
  setupFBXModel();
}

function setupFBXModel() {
  if (loadedScene.animations && loadedScene.animations.length) {
    setupAnimations();
  }
  adjustCameraToFitModel();
}

function handleGLTFLoaded(gltf) {
  scene.remove(loadedScene);
  loadedScene = gltf.scene;
  scene.add(loadedScene);

  skeletonMesh = createBlenderStyleSkeleton(loadedScene);
  setupGLTFModel(gltf);
}

function setupGLTFModel(gltf) {
  if (gltf.animations && gltf.animations.length) {
    setupGLTFAnimations(gltf.animations);
  }
  adjustCameraToFitModel();
}

function setupGLTFAnimations(animations) {
  const mixer = new THREE.AnimationMixer(loadedScene);
  mixers.push(mixer);
  animations.forEach((clip) => {
    const action = mixer.clipAction(clip);
    action.play();
    animationActions.push(action);
  });

  animationDetails.duration = mixer.clipAction(animations[0]).getClip().duration;
}

function createBlenderStyleSkeleton(skeleton) {
  const boneMeshes = [];

  skeleton.traverse((bone) => {
    if (bone.isBone) {
      bone.children.forEach((child) => {
        if (child.isBone) {
          const start = bone.getWorldPosition(new THREE.Vector3());
          const end = child.getWorldPosition(new THREE.Vector3());

          const direction = new THREE.Vector3().subVectors(end, start);
          const length = direction.length();

          const baseRadius = length * 0.1;
          const height = length;

          const geometry = new THREE.CylinderGeometry(
            0,
            baseRadius,
            height,
            4
          );

          const material = new THREE.MeshBasicMaterial({
            color: 'orange',
          });

          const edgeMaterial = new THREE.LineBasicMaterial({
            color: 'black',
          });

          const pyramid = new THREE.Mesh(geometry, material);

          const edges = new THREE.EdgesGeometry(geometry);
          const edgeLines = new THREE.LineSegments(edges, edgeMaterial);
          pyramid.add(edgeLines);

          const midpoint = start.clone().add(end).multiplyScalar(0.5);
          pyramid.position.copy(midpoint);

          const quaternion = new THREE.Quaternion();
          quaternion.setFromUnitVectors(
            new THREE.Vector3(0, 1, 0),
            direction.normalize()
          );
          pyramid.setRotationFromQuaternion(quaternion);

          pyramid.visible = false;
          scene.add(pyramid);

          boneMeshes.push({ mesh: pyramid, parent: bone, child });
        }
      });
    }
  });

  return boneMeshes;
}

function updateConeSkeleton(boneMeshes) {
  boneMeshes.forEach(({ mesh, parent, child }) => {
    const parentPosition = parent.getWorldPosition(new THREE.Vector3());
    const childPosition = child.getWorldPosition(new THREE.Vector3());

    const direction = new THREE.Vector3().subVectors(childPosition, parentPosition);

    const midpoint = parentPosition.clone().add(childPosition).multiplyScalar(0.5);
    mesh.position.copy(midpoint);


    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0), 
      direction.normalize() 
    );
    mesh.setRotationFromQuaternion(quaternion);

    mesh.scale.set(1, direction.length(), 1);
  });
}

function setupAnimations() {
  const mixer = new THREE.AnimationMixer(loadedScene);
  mixers.push(mixer);
  loadedScene.animations.forEach((clip) => {
    const action = mixer.clipAction(clip);
    action.play();
    animationActions.push(action); 
  });

  animationDetails.duration = mixer.clipAction(loadedScene.animations[0]).getClip().duration;
}

function adjustCameraToFitModel() {
  let targetObject = loadedScene; 

  const box = new THREE.Box3().setFromObject(targetObject);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const distance = size.length() * 1.5;

  if (distance === 0) {
    console.warn("Calculated distance is zero. Defaulting to a basic distance.");
    camera.position.set(center.x, center.y, center.z + 10); 
  } else {
    camera.position.set(center.x, center.y, center.z + distance);
  }

  camera.lookAt(center);
  controls.target.copy(center);
  controls.update();
}


function formatTime(seconds) {
  const pad = (num) => num.toString().padStart(2, "0");
  const secondsLeft = Math.floor(seconds % 60);
  const milliseconds = Math.floor((seconds % 1) * 1000); 

  return `${pad(secondsLeft)}:${milliseconds.toString().padStart(2, "0")}`;
}


function animate() {
  requestAnimationFrame(animate);
  const delta = clock.getDelta();

  if (isAnimationPlaying.value) {
    mixers.forEach(mixer => mixer.update(delta));
  } else {
    mixers.forEach(mixer => mixer.update(0));
  }

  if (skeletonMesh) {
    updateConeSkeleton(skeletonMesh);
  }

  if (animationActions.length > 0) {
    const currentMixerTime = animationActions[0].time % animationDetails.duration;
    animationDetails.currentTime = currentMixerTime;
    progressBarWidth.value = (currentMixerTime / animationDetails.duration) * 100;
  }

  controls.update();
  renderer.render(scene, camera);
}

function zoomIn() {
  const direction = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
  camera.position.addScaledVector(direction, -zoomStep);
  controls.update();
}

function zoomOut() {
  const direction = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
  camera.position.addScaledVector(direction, zoomStep);
  controls.update();
}

function togglePanMode() {
  isPanModeEnabled.value = !isPanModeEnabled.value;
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN,
  };

  if (isPanModeEnabled.value) {
    controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
    controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
  }

  controls.update();
}

function toggleSkeletonVisibility() {  

  if (skeletonMesh) {
    skeletonMesh.forEach(({ mesh }) => {
      mesh.visible = !mesh.visible;
    });
  }

  if (loadedScene) {
    loadedScene.visible = !loadedScene.visible;
  }

  controls.update();
}

function toggleAnimation() {
  isAnimationPlaying.value = !isAnimationPlaying.value;
  iconName.value = isAnimationPlaying.value ? "pause" : "play";

  animationActions.forEach(action => {
    action.paused = !isAnimationPlaying.value;
    if (!isAnimationPlaying.value) {
      action.play();
    }
  });

  if (!isAnimationPlaying.value) {
    currentFrame.value = Math.round(animationDetails.currentTime * 30);
  }
}


function addGridFloor() {
  const gridHelper = new THREE.GridHelper(500, 50, 0x888888, 0x444444);
  gridHelper.position.y = 0;
  gridHelper.material.opacity = 0.75;
  gridHelper.material.transparent = true;
  scene.add(gridHelper);
}

function resetCamera() {
  adjustCameraToFitModel();
}

function setAnimationProgress(event) {
  const bounds = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - bounds.left;
  const progressPercentage = clickX / bounds.width;
  const newTime = progressPercentage * animationDetails.duration;


  animationActions.forEach(action => {
    action.paused = true; 
    action.time = newTime;
    action.play(); 
    action.paused = !isAnimationPlaying.value; 
  });

  animationDetails.currentTime = newTime;
  progressBarWidth.value = progressPercentage * 100;
}

function handleDragOver(event) {
  event.preventDefault();
}

function downloadModel() {
  if (!modelBuffer) {
    console.error("Model buffer is not available.");
    return;
  }

  const isGLB = checkIfGLB(modelBuffer);
  const blob = new Blob([modelBuffer], { type: isGLB ? 'model/gltf-binary' : 'model/fbx' });
  const link = document.createElement('a');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.href = URL.createObjectURL(blob);
  link.download = isGLB ? 'model.glb' : 'model.fbx';
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

function startDragging(event) {
  dragging.value = true;
  updateAnimationProgress(event);
}

function dragUpdate(event) {
  if (dragging.value) {
    updateAnimationProgress(event);
  }
}

function stopDragging() {
  dragging.value = false;
}

function updateAnimationProgress(event) {
  const bounds = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - bounds.left;
  const progressPercentage = clickX / bounds.width;
  const newTime = progressPercentage * animationDetails.duration;

  animationActions.forEach(action => {
    action.paused = true; 
    action.time = newTime; 
    action.play();
    action.paused = !isAnimationPlaying.value; 
  });

  animationDetails.currentTime = newTime;
  progressBarWidth.value = progressPercentage * 100;
}

defineExpose({
  downloadModel
});
</script>

<style scoped>
.anim-controls,
.scale-controls {
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.control-btn {
  padding: 10px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.progress-bar {
  background-color: white;
  height: .7rem;
  width: 0%;
  min-width: 4px;
}

.animation-wrapper {
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.threejs-container {
  margin: auto;
  width: 90%;
  height: 70vh;
}

@media (max-height: 750px) {
    .threejs-container {
    margin: auto;
    width: 90%;
    height: 60vh;
  }
}

.animation-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.playbar {
  width: 40%;
}

.progress-container {
  flex-grow: 1;
  height: .7rem;
  background-color: darkgrey;
  border-radius: 4px;
  margin: 0 10px;
  position: relative;
  cursor: pointer;
  margin-bottom: 5px;
  user-select: none; 
}

.time-codes {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.playbar-controls {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
}

.caption {
  color: rgba(255, 255, 255, .4);
}

.loader-description {
  margin-bottom: 10px;
}

.locked {
  cursor: not-allowed;
}

.rotate {
  transform: rotate(180deg);
}

.progress-placeholder {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
  width: 50%;
  height: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  background-size: cover;
  background-repeat: no-repeat;
  background-clip: padding-box;
  background-position: center;
  border: 1rem solid rgba(255, 255, 255, 0.1); 
}

.progress-circle-bg,
.progress-circle-fg {
  transition: stroke-dashoffset 0.3s linear;
}
</style>
